/**
 * Centralized status configuration for eScribe
 *
 * Uses Deep Trust theme with status dot indicators:
 * - Teal: completed, sent, signed, active
 * - Amber: pending, in-progress, partially signed
 * - Sky/Blue: viewed, delivered, info
 * - Red: declined, voided, expired
 * - Slate: draft, voided, inactive
 */

// Envelope/Document status configuration
export const ENVELOPE_STATUS = {
  draft: {
    key: "draft",
    variant: "draft",
    labelKey: "envelopes.status.draft",
  },
  pending: {
    key: "pending",
    variant: "pending",
    labelKey: "envelopes.status.pending",
  },
  sent: {
    key: "sent",
    variant: "sent",
    labelKey: "envelopes.status.sent",
  },
  viewed: {
    key: "viewed",
    variant: "viewed",
    labelKey: "envelopes.status.viewed",
  },
  partially_signed: {
    key: "partially_signed",
    variant: "partially-signed",
    labelKey: "envelopes.status.partiallySigned",
  },
  completed: {
    key: "completed",
    variant: "completed",
    labelKey: "envelopes.status.completed",
  },
  declined: {
    key: "declined",
    variant: "declined",
    labelKey: "envelopes.status.declined",
  },
  voided: {
    key: "voided",
    variant: "voided",
    labelKey: "envelopes.status.voided",
  },
  expired: {
    key: "expired",
    variant: "expired",
    labelKey: "envelopes.status.expired",
  },
};

// Recipient status configuration
export const RECIPIENT_STATUS = {
  pending: {
    key: "pending",
    variant: "pending",
    labelKey: "envelopeDetail.recipientStatus.pending",
  },
  sent: {
    key: "sent",
    variant: "sent",
    labelKey: "envelopeDetail.recipientStatus.sent",
  },
  delivered: {
    key: "delivered",
    variant: "sent",
    labelKey: "envelopeDetail.recipientStatus.delivered",
  },
  viewed: {
    key: "viewed",
    variant: "viewed",
    labelKey: "envelopeDetail.recipientStatus.viewed",
  },
  signed: {
    key: "signed",
    variant: "completed",
    labelKey: "envelopeDetail.recipientStatus.signed",
  },
  declined: {
    key: "declined",
    variant: "declined",
    labelKey: "envelopeDetail.recipientStatus.declined",
  },
};

// Payment/Invoice status configuration
export const PAYMENT_STATUS = {
  pending: {
    key: "pending",
    variant: "pending",
    labelKey: "billing.status.pending",
  },
  paid: {
    key: "paid",
    variant: "completed",
    labelKey: "billing.status.paid",
  },
  failed: {
    key: "failed",
    variant: "declined",
    labelKey: "billing.status.failed",
  },
  refunded: {
    key: "refunded",
    variant: "voided",
    labelKey: "billing.status.refunded",
  },
};

// Subscription/Addon status configuration
export const SUBSCRIPTION_STATUS = {
  active: {
    key: "active",
    variant: "completed",
    labelKey: "common.active",
  },
  inactive: {
    key: "inactive",
    variant: "voided",
    labelKey: "common.inactive",
  },
  trialing: {
    key: "trialing",
    variant: "info",
    labelKey: "common.trial",
  },
  past_due: {
    key: "past_due",
    variant: "pending",
    labelKey: "common.pastDue",
  },
  canceled: {
    key: "canceled",
    variant: "declined",
    labelKey: "common.canceled",
  },
};

// Plan tier configuration
export const PLAN_TIERS = {
  free: {
    key: "free",
    variant: "plan-free",
    labelKey: "plans.free",
  },
  starter: {
    key: "starter",
    variant: "plan-starter",
    labelKey: "plans.starter",
  },
  professional: {
    key: "professional",
    variant: "plan-professional",
    labelKey: "plans.professional",
  },
  enterprise: {
    key: "enterprise",
    variant: "plan-enterprise",
    labelKey: "plans.enterprise",
  },
};

// Role configuration
export const ROLES = {
  signer: {
    key: "signer",
    variant: "role-signer",
    labelKey: "roles.signer",
  },
  reviewer: {
    key: "reviewer",
    variant: "role-reviewer",
    labelKey: "roles.reviewer",
  },
  cc: {
    key: "cc",
    variant: "role-cc",
    labelKey: "roles.cc",
  },
  approver: {
    key: "approver",
    variant: "role-approver",
    labelKey: "roles.approver",
  },
  admin: {
    key: "admin",
    variant: "role-admin",
    labelKey: "roles.admin",
  },
};

// Certificate status configuration
export const CERTIFICATE_STATUS = {
  valid: {
    key: "valid",
    variant: "completed",
    labelKey: "certificates.status.valid",
  },
  expired: {
    key: "expired",
    variant: "expired",
    labelKey: "certificates.status.expired",
  },
  revoked: {
    key: "revoked",
    variant: "declined",
    labelKey: "certificates.status.revoked",
  },
};

// Template status configuration
export const TEMPLATE_STATUS = {
  active: {
    key: "active",
    variant: "completed",
    labelKey: "templates.status.active",
  },
  draft: {
    key: "draft",
    variant: "draft",
    labelKey: "templates.status.draft",
  },
  archived: {
    key: "archived",
    variant: "voided",
    labelKey: "templates.status.archived",
  },
};

// Generic utility to get status config
export const getStatusConfig = (statusMap, status) =>
  statusMap[status] || { variant: "muted", labelKey: status };

// Statuses that should show dot indicators
export const STATUS_WITH_DOTS = [
  "draft",
  "pending",
  "in-progress",
  "sent",
  "viewed",
  "completed",
  "declined",
  "voided",
  "expired",
  "partially-signed",
  "partially_signed",
];

// Check if a variant should show a dot
export const shouldShowDot = (variant) => STATUS_WITH_DOTS.includes(variant);
